import { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, X, Check, Calendar as CalIcon, Sparkles, Trophy } from "lucide-react";

const DAY_LABELS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const TYPE_COLORS = {
  easy: "#71c4ff", long: "#d7ff00", threshold: "#ff8800", tempo: "#ff8800",
  vo2: "#ff3b30", hill: "#ff6b00", recovery: "#7fffd4", strength: "#c084fc",
  mobility: "#a3e635", rest: "#444",
};

export default function CalendarView() {
  const [full, setFull] = useState(null);
  const [cursor, setCursor] = useState(() => new Date()); // shows month containing cursor
  const [selected, setSelected] = useState(null); // session detail modal
  const [feel, setFeel] = useState("good");
  const [rpe, setRpe] = useState(6);
  const [notes, setNotes] = useState("");
  const [actualDistance, setActualDistance] = useState("");
  const [actualDuration, setActualDuration] = useState("");
  const [actualPace, setActualPace] = useState("");
  const [intervals, setIntervals] = useState("");
  const [saving, setSaving] = useState(false);
  const [dragSrc, setDragSrc] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [moving, setMoving] = useState(false);

  const load = () => api.get("/training/full").then(r => setFull(r.data)).catch(() => setFull({ program: null, weeks: [] }));
  useEffect(() => { load(); }, []);

  const moveSession = async (src, tgtIso) => {
    if (!src || !tgtIso) return;
    // Find target session for swap
    const tgt = sessionsByDate[tgtIso];
    // Compute target week/day from date
    const tgtDate = new Date(tgtIso + "T12:00:00");
    // Find week_index by matching against weeks
    let to_week = -1, to_day = -1;
    full.weeks.forEach(w => {
      const wStart = new Date(w.starts_on + "T00:00:00");
      const diff = Math.floor((tgtDate - wStart) / (1000 * 60 * 60 * 24));
      if (diff >= 0 && diff < 7) { to_week = w.week_index; to_day = diff; }
    });
    if (to_week < 0) return;
    setMoving(true);
    try {
      await api.post("/training/sessions/move", {
        from_week: src.original_week_index ?? src.week_index,
        from_day: src.original_day_index ?? src.day_index,
        to_week, to_day,
      });
      toast.success(tgt ? `Swapped with ${tgt.name}` : `Moved to ${tgtIso}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Move failed");
    }
    setMoving(false);
    setDragSrc(null);
    setDropTarget(null);
  };

  // Build a map: date -> session
  const sessionsByDate = useMemo(() => {
    const m = {};
    full?.weeks?.forEach(w => w.sessions.forEach(s => { m[s.date] = { ...s, week_index: w.week_index, week_number: w.week_number }; }));
    return m;
  }, [full]);

  // Build month grid (starts Monday)
  const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  const gridStart = new Date(monthStart);
  const startWeekday = (monthStart.getDay() + 6) % 7; // 0 = Mon
  gridStart.setDate(monthStart.getDate() - startWeekday);
  const cells = [];
  const d = new Date(gridStart);
  for (let i = 0; i < 42; i++) {
    const iso = d.toISOString().slice(0, 10);
    cells.push({ date: new Date(d), iso, inMonth: d.getMonth() === cursor.getMonth(), session: sessionsByDate[iso] });
    d.setDate(d.getDate() + 1);
  }

  const todayIso = new Date().toISOString().slice(0, 10);

  const monthSessionCount = cells.filter(c => c.inMonth && c.session).length;
  const monthKm = cells.filter(c => c.inMonth && c.session).reduce((sum, c) => sum + (c.session.distance_km || 0), 0);
  const monthDone = cells.filter(c => c.inMonth && c.session?.completed).length;

  const monthLabel = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const completeSession = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.post(`/training/sessions/${selected.day_index}/complete`, {
        feel, rpe: Number(rpe), notes, week_index: selected.week_index,
        actual_distance_km: actualDistance ? Number(actualDistance) : null,
        actual_duration_min: actualDuration ? Number(actualDuration) : null,
        actual_pace: actualPace || null,
        intervals: intervals || null,
      });
      window.dispatchEvent(new CustomEvent("session:logged"));
      toast.success("Session marked complete");
      setSelected(null);
      setNotes(""); setActualDistance(""); setActualDuration(""); setActualPace(""); setIntervals("");
      load();
    } catch { toast.error("Could not save"); }
    setSaving(false);
  };

  if (!full) return <AppShell><div className="text-[#888] font-mono-jp text-xs uppercase tracking-widest">Loading…</div></AppShell>;

  if (!full.program) {
    return (
      <AppShell>
        <div data-testid="calendar-no-program">
          <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">CALENDAR</div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase">Monthly <span className="text-[#d7ff00]">overview</span></h1>
          <div className="mt-8 bg-[#0a0a0a] border border-[#1f1f1f] p-8 text-center">
            <Sparkles className="w-10 h-10 text-[#d7ff00] mx-auto" />
            <h2 className="mt-4 font-display text-2xl uppercase">No program yet</h2>
            <p className="mt-2 text-[#bbb] text-sm max-w-md mx-auto">Coach JP is preparing your tailored program. Once published, your full 8-16 week plan will appear here.</p>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div data-testid="calendar-page">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">CALENDAR · {full.program.name?.toUpperCase()}</div>
            <h1 className="font-display text-4xl sm:text-5xl uppercase">{monthLabel}</h1>
            <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] mt-1">
              {full.total_weeks}-WEEK PLAN · WEEK {full.current_week_index + 1} OF {full.total_weeks} · STARTED {full.started_at}
            </div>
          </div>
          <div className="flex gap-px bg-[#1f1f1f]">
            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="bg-[#0a0a0a] px-3 py-2 text-[#d7ff00] hover:bg-[#1a1a1a]" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => setCursor(new Date())} className="bg-[#0a0a0a] px-4 py-2 font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb] hover:text-white" data-testid="cal-today">TODAY</button>
            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="bg-[#0a0a0a] px-3 py-2 text-[#d7ff00] hover:bg-[#1a1a1a]" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Stats row */}
        <div className="mt-6 grid grid-cols-3 gap-px bg-[#1f1f1f]">
          <StatTile label="SESSIONS" value={monthSessionCount} sub="this month" />
          <StatTile label="VOLUME" value={monthKm.toFixed(0)} sub="km planned" accent />
          <StatTile label="COMPLETED" value={monthDone} sub={`/ ${monthSessionCount}`} accent />
        </div>

        {/* Calendar grid */}
        <div className="mt-6">
          <div className="grid grid-cols-7 text-center">
            {DAY_LABELS.map(d => (
              <div key={d} className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] py-2">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-px bg-[#1f1f1f]" data-testid="calendar-grid">
            {cells.map((c, i) => {
              const isToday = c.iso === todayIso;
              const s = c.session;
              const color = s ? (TYPE_COLORS[s.type] || "#d7ff00") : null;
              return (
                <button
                  key={i}
                  onClick={() => s && setSelected(s)}
                  disabled={!s}
                  className={`relative bg-[#0a0a0a] min-h-[78px] sm:min-h-[100px] p-1.5 sm:p-2 text-left flex flex-col ${c.inMonth ? "" : "opacity-30"} ${s ? "hover:bg-[#111]" : ""} ${isToday ? "ring-1 ring-[#d7ff00] z-10" : ""}`}
                  data-testid={s ? `day-${c.iso}` : `day-empty-${c.iso}`}>
                  <span className={`font-mono-jp text-[10px] ${isToday ? "text-[#d7ff00]" : "text-[#666]"}`}>{c.date.getDate()}</span>
                  {s && (
                    <div className="mt-1 flex-1 flex flex-col gap-0.5">
                      <div className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: color }} />
                        <span className="font-mono-jp text-[8px] sm:text-[9px] uppercase tracking-wider text-[#bbb] truncate">W{s.week_number}·{s.type?.slice(0, 4)}</span>
                      </div>
                      <div className="font-display text-[10px] sm:text-xs uppercase tracking-wide truncate leading-tight">{s.name}</div>
                      {s.distance_km && <div className="font-mono-jp text-[9px] text-[#888]">{s.distance_km}km</div>}
                      {s.completed && <Check className="w-3 h-3 text-[#d7ff00] absolute top-1 right-1" />}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Legend */}
        <div className="mt-6 flex flex-wrap gap-2 font-mono-jp text-[10px] uppercase tracking-widest text-[#888]" data-testid="cal-legend">
          {[["easy","Easy"],["long","Long"],["threshold","Threshold"],["vo2","VO2 max"],["hill","Hills"],["recovery","Recovery"],["strength","Strength"]].map(([t, l]) => (
            <div key={t} className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ background: TYPE_COLORS[t] }} /> {l}
            </div>
          ))}
        </div>

        {/* Week-by-week summary list */}
        <div className="mt-8">
          <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] mb-3">FULL {full.total_weeks}-WEEK PROGRESSION</div>
          <div className="space-y-2" data-testid="weeks-list">
            {full.weeks.map(w => {
              const done = w.sessions.filter(s => s.completed).length;
              const isCurrent = w.week_index === full.current_week_index;
              const isPast = new Date(w.ends_on) < new Date();
              return (
                <div key={w.week_index} className={`bg-[#0a0a0a] border ${isCurrent ? "border-[#d7ff00]" : "border-[#1f1f1f]"} p-3 sm:p-4 flex items-center justify-between gap-3`} data-testid={`week-row-${w.week_index}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className={`font-display text-base sm:text-lg uppercase ${isCurrent ? "text-[#d7ff00]" : ""}`}>Week {w.week_number}</div>
                      {isCurrent && <span className="font-mono-jp text-[9px] uppercase tracking-widest text-[#d7ff00] border border-[#d7ff00] px-1.5">NOW</span>}
                      {w.week_index === full.total_weeks - 1 && <Trophy className="w-3.5 h-3.5 text-[#d7ff00]" />}
                    </div>
                    <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#666]">{w.starts_on} → {w.ends_on}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">{w.sessions.length} sessions · {w.total_km}km</div>
                    {isPast && <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">{done}/{w.sessions.length} done</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Session detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => !saving && setSelected(null)} data-testid="session-modal">
          <div className="bg-[#0a0a0a] border border-[#d7ff00] w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">WEEK {selected.week_number} · {DAY_LABELS[selected.day_index]} · {selected.date}</div>
                <h3 className="font-display text-2xl sm:text-3xl uppercase mt-1">{selected.name}</h3>
              </div>
              {!saving && <button onClick={() => setSelected(null)} className="text-[#888]" data-testid="session-close"><X /></button>}
            </div>
            <p className="text-[#bbb] text-sm">{selected.description}</p>
            <div className="mt-3 flex flex-wrap gap-2 font-mono-jp text-[10px] uppercase tracking-widest">
              {selected.distance_km && <span className="border border-[#222] px-2 py-1 text-[#bbb]">{selected.distance_km} KM</span>}
              {selected.duration_min && <span className="border border-[#222] px-2 py-1 text-[#bbb]">{selected.duration_min} MIN</span>}
              {selected.pace_target && <span className="border border-[#222] px-2 py-1 text-[#bbb]">{selected.pace_target}</span>}
              {selected.hr_zone && <span className="border border-[#222] px-2 py-1 text-[#d7ff00]">{selected.hr_zone}</span>}
              <span className="border border-[#222] px-2 py-1 capitalize" style={{ color: TYPE_COLORS[selected.type] || "#d7ff00" }}>{selected.type}</span>
            </div>

            {selected.completed ? (
              <div className="mt-5 bg-[#1a1f00] border border-[#d7ff00]/40 p-3">
                <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] flex items-center gap-1"><Check className="w-3 h-3" /> COMPLETED</div>
                {selected.feedback?.feel && <div className="text-sm text-white mt-2">Feel: <span className="text-[#d7ff00]">{selected.feedback.feel}</span> · RPE {selected.feedback.rpe || "—"}</div>}
                {(selected.feedback?.actual_distance_km || selected.feedback?.actual_duration_min || selected.feedback?.actual_pace) && (
                  <div className="text-sm text-[#ddd] mt-1">
                    {selected.feedback.actual_distance_km && <span className="mr-3">{selected.feedback.actual_distance_km} km</span>}
                    {selected.feedback.actual_duration_min && <span className="mr-3">{selected.feedback.actual_duration_min} min</span>}
                    {selected.feedback.actual_pace && <span>@ {selected.feedback.actual_pace}</span>}
                  </div>
                )}
                {selected.feedback?.intervals && <div className="text-sm text-white mt-2"><span className="font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb]">Intervals:</span><br />{selected.feedback.intervals}</div>}
                {selected.feedback?.notes && <div className="text-sm text-white mt-2"><span className="font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb]">Notes:</span><br />{selected.feedback.notes}</div>}
              </div>
            ) : (
              <>
                <div className="mt-5 font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb]">How was it?</div>
                <div className="mt-2 grid grid-cols-3 sm:grid-cols-6 gap-1">
                  {["great","good","average","tired","very_tired","pain"].map(f => (
                    <button key={f} onClick={() => setFeel(f)} className={`px-2 py-2 font-mono-jp text-[10px] uppercase tracking-widest border ${feel === f ? "border-[#d7ff00] text-[#d7ff00] bg-[#d7ff00]/10" : "border-[#222] text-[#bbb]"}`} data-testid={`feel-${f}`}>{f.replace("_"," ")}</button>
                  ))}
                </div>
                <div className="mt-3 font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb]">RPE · <span className="text-[#d7ff00]">{rpe}/10</span></div>
                <input type="range" min={1} max={10} value={rpe} onChange={e => setRpe(e.target.value)} className="w-full accent-[#d7ff00]" data-testid="rpe-slider" />

                <div className="mt-3 grid grid-cols-3 gap-2">
                  <div>
                    <label className="font-mono-jp text-[9px] uppercase tracking-widest text-[#bbb]">Distance (km)</label>
                    <input type="number" step="0.1" value={actualDistance} onChange={e => setActualDistance(e.target.value)} placeholder={selected.distance_km ? String(selected.distance_km) : "—"} className="mt-1 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-2.5 py-2 text-sm text-white" data-testid="actual-distance" />
                  </div>
                  <div>
                    <label className="font-mono-jp text-[9px] uppercase tracking-widest text-[#bbb]">Duration (min)</label>
                    <input type="number" value={actualDuration} onChange={e => setActualDuration(e.target.value)} placeholder={selected.duration_min ? String(selected.duration_min) : "—"} className="mt-1 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-2.5 py-2 text-sm text-white" data-testid="actual-duration" />
                  </div>
                  <div>
                    <label className="font-mono-jp text-[9px] uppercase tracking-widest text-[#bbb]">Pace</label>
                    <input value={actualPace} onChange={e => setActualPace(e.target.value)} placeholder={selected.pace_target || "5:12"} className="mt-1 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-2.5 py-2 text-sm text-white" data-testid="actual-pace" />
                  </div>
                </div>

                <label className="mt-3 block font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb]">Interval splits (optional)</label>
                <textarea rows={2} placeholder="e.g. 400m × 8: 1:25, 1:24, 1:26…" value={intervals} onChange={e => setIntervals(e.target.value)} className="mt-1 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-3 py-2 text-sm text-white" data-testid="session-intervals" />

                <label className="mt-3 block font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb]">Note to Coach JP</label>
                <textarea rows={3} placeholder="How did it feel? Anything Coach should know?" value={notes} onChange={e => setNotes(e.target.value)} className="mt-1 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-3 py-2 text-sm text-white" data-testid="session-notes" />

                <button onClick={completeSession} disabled={saving} className="mt-4 w-full bg-[#d7ff00] text-black font-display uppercase tracking-wider py-3 hover:bg-white disabled:opacity-50" data-testid="mark-complete-btn">
                  {saving ? "Saving…" : "Mark complete"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </AppShell>
  );
}

function StatTile({ label, value, sub, accent }) {
  return (
    <div className="bg-[#0a0a0a] p-4">
      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">{label}</div>
      <div className={`mt-2 font-mono-jp text-3xl tick ${accent ? "text-[#d7ff00]" : "text-white"}`}>{value}</div>
      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#666]">{sub}</div>
    </div>
  );
}
